import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Avatar,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Spin,
  Tooltip,
  message,
  Popconfirm,
  Modal,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  EditOutlined,
  DragOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import UserIcon from "../../../../assets/images/icons/userLogo.svg";
import { useLocation, useParams } from "react-router-dom";
import { useStore } from "../../../../store";
import { TAB_KEYS } from "../../../../config";
import { useTab } from "../../../../custom-hooks/useTab";
import { useConnectsuForm } from "../../../../custom-hooks/useConnectsuForm";
import { getFormFieldValue } from "../../../../utils";
import { sortDataByOrder } from "../../../../utils/helper";
import "../../../slider/imageSlider.scss";
import ReactPlayer from "react-player/youtube";
import { deleteRequest, getRequest, putRequest } from "../../../../api/http";
import { Option } from "antd/lib/mentions";
import userServiceApi from "../../../../api/userServiceApi";
import SynthesiaVideo from "./SynthesiaVideo";
import { toast } from "react-toastify";
import reArrangeOrder from "../../../common/DragAndDropList/rearrangeHelper";
import {
  DragAndDropList,
  GifCreator,
  GifResultPreview,
  createEmptyGifState,
  withGifDefaults,
} from "../../../common";
import "./TeamsRoles.scss";

/**
 * Roles list order:
 * - Prefer saved DnD `order` when present
 * - Otherwise oldest `createdAt` first (newest last)
 */
const sortRolesForDisplay = (data) => {
  if (!data?.length) return [];
  return [...data].sort((a, b) => {
    const aOrder = a?.order != null ? Number(a.order) : Number.POSITIVE_INFINITY;
    const bOrder = b?.order != null ? Number(b.order) : Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) return aOrder - bOrder;
    const aTime = new Date(a?.createdAt || 0).getTime();
    const bTime = new Date(b?.createdAt || 0).getTime();
    return aTime - bTime;
  });
};

const { TextArea } = Input;
const MAX_CHARACTERS = 500;
const levelIndexMap = {
  "Level 1": 1,
  "Level 2": 2,
  "Level 3": 3,
  "Level 4": 4,
  "Level 5": 5,
  "Level 6": 6,
  "Level 7": 7,
  "Level 8": 8,
  "Entry Level": 0,
};

const SortableTeamRow = ({ team, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: team._key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return children({ setNodeRef, attributes, listeners, style });
};

export default function TeamsAndRoles({ dataLoader, form }) {
  const { micrositeId } = useParams();
  const { state } = useLocation();
  const inputRef = useRef(null);
  const tabKey = state;
  const { tabData, updatedValues } = useStore();
  const whichTab = TAB_KEYS.teamsAndRoles;
  const { deleteSingleItem, postLoader } = useTab();
  const { updateImage } = useConnectsuForm();
  const [loader, setLoader] = useState(true);
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [formChanged, setFormChanged] = useState(false);
  const levelColor = [
    "#ff0000",
    "#ff9900",
    "#332206",
    "#b6d7a8",
    "#00ffff",
    "#0000ff",
    "#9900ff",
    "#6aa84f",
    "#ff00ff",
  ];

  const [canAddNew, setCanAddNew] = useState(true);
  const [hasUnsavedItem, setHasUnsavedItem] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const tabConfigId = localStorage?.getItem("selectedTabId");
  const [teamsList, setTeamsList] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(null);
  const [expandedRoleKey, setExpandedRoleKey] = useState(null);

  const [manageTeamsOpen, setManageTeamsOpen] = useState(false);
  const [draftTeams, setDraftTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [editingTeamKey, setEditingTeamKey] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState("");
  const [savingTeams, setSavingTeams] = useState(false);

  const emptyRow = {
    id: undefined,
    assetTitleId: undefined,
    subTitle: "",
    levelId: undefined,
    description: "",
    media: {},
    mediaLink: { mediaType: undefined, url: "", gifCreator: undefined },
  };

  const fetchTeamsSorted = async () => {
    const res = await getRequest(
      userServiceApi.getSectionTitles(micrositeId, tabConfigId)
    );
    const sorted = sortDataByOrder(
      (res?.data || []).filter(
        (item) => item?.title != null && String(item.title).trim() !== ""
      )
    );
    setTeamsList(sorted);
    return sorted;
  };

  useEffect(() => {
    if (micrositeId && tabConfigId) {
      fetchTeamsSorted().catch((error) => toast.error(error.error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micrositeId, tabConfigId]);

  useEffect(() => {
    if (!teamsList.length) {
      setActiveTeamId(null);
      return;
    }
    const stillExists = teamsList.some((t) => String(t.id) === String(activeTeamId));
    if (!activeTeamId || !stillExists) {
      setActiveTeamId(teamsList[0].id);
    }
  }, [teamsList, activeTeamId]);

  useEffect(() => {
    localStorage.setItem("tab", "TEAMS_AND_ROLES");
  }, []);

  useEffect(() => {
    const isNotEmpty = Object.keys(updatedValues).length > 0;
    setFormChanged(isNotEmpty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(updatedValues).length]);

  const renderMedia = (dataItem, rowIndex) => {
    const commonStyles = {
      display: "flex",
      justifyContent: "center",
    };

    if (
      dataItem?.mediaLink?.mediaType === "SOCIAL_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <ReactPlayer
            controls={true}
            url={dataItem.mediaLink.url}
            width="100%"
            height="100%"
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "VIMEO_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <iframe
            title="Embedded video"
            src={dataItem.mediaLink.url}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            width="100%"
            height="100%"
            style={{ backgroundColor: "black" }}
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "IMAGE" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <img
            src={dataItem?.mediaLink?.url}
            alt=""
            className="c1 carousel-image"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </div>
      );
    } else if (dataItem?.mediaLink?.mediaType === "GIF") {
      const gifState = dataItem.mediaLink.gifCreator || {};
      const gifUrl = gifState.previewUrl || dataItem.mediaLink?.url;
      return (
        <GifResultPreview
          url={gifUrl}
          fileName={gifState.fileName}
          loading={!!gifState.generating}
          showEdit={!!gifUrl && gifState.isEditing === false}
          onEdit={() => {
            const current = form.getFieldValue([
              whichTab,
              rowIndex,
              "mediaLink",
              "gifCreator",
            ]);
            form.setFieldValue(
              [whichTab, rowIndex, "mediaLink", "gifCreator"],
              { ...createEmptyGifState(), ...current, isEditing: true }
            );
          }}
        />
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <SynthesiaVideo key={dataItem.id} videoData={dataItem} />
        </div>
      );
    }

    return null;
  };

  const findValue = (i, key) => {
    return getFormFieldValue(form, whichTab, i, key);
  };

  const SelectChangeHandler = (e, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], "");
    if (e === "GIF") {
      form.setFieldValue(
        [whichTab, i, "mediaLink", "gifCreator"],
        createEmptyGifState()
      );
    } else {
      form.setFieldValue([whichTab, i, "mediaLink", "gifCreator"], undefined);
    }
  };

  useEffect(() => {
    form.resetFields();
    if (tabData && tabData[whichTab] && tabData[whichTab].length > 0) {
      form.setFieldsValue({
        [whichTab]: withGifDefaults(sortRolesForDisplay(tabData[whichTab])),
      });
    }
    refreshUnsavedState();
    setTimeout(() => {
      setLoader(false);
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabData[whichTab]]);

  const isTeamRole = (item) =>
    activeTeamId != null && String(item?.assetTitleId) === String(activeTeamId);

  const refreshUnsavedState = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const unsavedItem = currentList.find((item) => {
      if (activeTeamId && !isTeamRole(item)) return false;
      const hasData =
        item?.subTitle?.trim() ||
        item?.description?.trim() ||
        item?.levelId ||
        item?.mediaLink?.url?.trim() ||
        item?.mediaLink?.mediaType ||
        item?.media ||
        item?.mediaLink?.gifCreator?.images?.length ||
        item?.mediaLink?.gifCreator?.generatedFile;
      return !item?.id && hasData;
    });

    const unsavedEmpty = currentList.find(
      (item) => activeTeamId && isTeamRole(item) && !item?.id
    );

    setCanAddNew(!unsavedEmpty);
    setHasUnsavedItem(!!unsavedItem || !!unsavedEmpty);
  };

  const handleAddMore = () => {
    if (!activeTeamId) {
      message.warning("Please add a team before adding a role.");
      return;
    }

    if (!canAddNew) {
      message.warning(
        "You must save or clear the current item before adding a new one."
      );
      return;
    }

    const currentList = form.getFieldValue(whichTab) || [];
    const newRow = {
      ...emptyRow,
      assetTitleId: activeTeamId,
    };
    // Append new role at the end of the list
    const newList = [...currentList, newRow];
    form.setFieldsValue({ [whichTab]: newList });

    setCanAddNew(false);
    setHasUnsavedItem(true);
    setExpandedRoleKey("new-role");
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 50);
  };

  const handleClear = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const firstUnsavedIndex = currentList.findIndex(
      (item) => !item?.id && isTeamRole(item)
    );

    if (firstUnsavedIndex === -1) {
      message.warning("No unsaved item to clear.");
      return;
    }

    const updatedList = currentList.map((item, idx) =>
      idx === firstUnsavedIndex
        ? {
            ...emptyRow,
            assetTitleId: activeTeamId,
          }
        : item
    );

    form.setFieldsValue({ [whichTab]: updatedList });
    refreshUnsavedState();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tabData?.["teams-and-roles"]?.length === 0) {
        setHasUnsavedItem(false);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabData?.["teams-and-roles"]]);

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const response = await getRequest(
          userServiceApi.getMasterDataType("LEVEL"),
          ""
        );
        const data = response.data;
        setLevels(data);

        if (data.length > 0) {
          setSelectedLevel(data[0].id.toString());
        }
      } catch (error) {
        console.error("Error fetching levels:", error);
      }
    };

    fetchLevels();
  }, []);

  const levelsData = [
    {
      id: 0,
      value: "Entry Level",
      additionalInfo:
        "Qualifications for Entry Level are:\n Entry Level Award\n Entry Level Certificate (ELC)\n Entry Level diploma\n Entry Level English for speakers of other languages(ESOL)\n Entry Level essential skills\n  Entry Level functional skills\n Skills for Life",
    },
    {
      id: 1,
      value: "Level 1",
      additionalInfo:
        "Level 1 Qualifications are:\n first certificate\n GCSE- grades 3,2,1 or grades D,E,F,G\n Level 1 award\n Level 1 certificate\n Level 1 diploma\n Level 1 ESOL\n Level 1 essential skills\n level 1 functional skills\n level 1 national vocational qualification(NVQ)\n Music grades 1,2 and 3",
    },
    {
      id: 2,
      value: "Level 2",
      additionalInfo:
        "Level 2 Qualifications are:\n CSE- grade 1\nGCSE-grades 9,8,7,6,5,4 or grades A*,A,B,C\n intermediate apprenticeship\n Level 2 award\n Level 2 certificate\n Level 2 diploma\n Level 2 ESOL\n Level 2 essential skills\n Level 2 Functional skills\n Level 2 national certificate\n Level 2 national diploma\n Level 2 NVQ\n Music grades 4 and 5\n O-Level- grade A,B or C ",
    },
    {
      id: 3,
      value: "Level 3",
      additionalInfo:
        "Level 3 Qualifications are:\n A Level\n Access to higher education diploma\n Advanced apprenticeship\n applied general\n AS level\n international Baccalaureate diploma\n Level 3 award\n Level 3 certificate\n Level 3 diploma\n Level 3 ESOL\n Level 3 national certificate\n Level 3 national diploma\n Level 3 NVQ\n music grades 6,7 and 8\n T Level\n Tech level",
    },
    {
      id: 4,
      value: "Level 4",
      additionalInfo:
        "Level 4 Qualifications are:\n Certificate of higher education(CertHE)\n Higher apprenticeship\n Higher national Certificate\nLevel 4 award\n Level 4 certificate\n Level 4 diploma\n n Level 4 NVQ",
    },
    {
      id: 5,
      value: "Level 5",
      additionalInfo:
        "Level 5 qualifications are:\n diploma of higher education (DipHE)\n foundation degree\n higher national diploma (HND)\n level 5 award\n level 5 certificate\n level 5 diploma\n level 5 NVQ",
    },
    {
      id: 6,
      value: "Level 6",
      additionalInfo:
        "Level 6 qualifications are:\n degree apprenticeship\n degree with honours for example bachelor of the arts (BA) hons, bachelor of science (BSc) hons\n graduate certificate\n graduate diploma\n level 6 award\n level 6 certificate\n level 6 diploma\n level 6 NVQ\n ordinary degree without honours",
    },
    {
      id: 7,
      value: "Level 7",
      additionalInfo:
        "Level 7 qualifications are:\n integrated master's degree, for example master of engineering (MEng)\n level 7 award\n level 7 certificate\n level 7 diploma\n level 7 NVQ\n master's degree, for example master of arts (MA), master of science (MSc)\n postgraduate certificate\n postgraduate certificate in education (PGCE)\n postgraduate diploma",
    },
    {
      id: 8,
      value: "Level 8",
      additionalInfo:
        "Level 8 qualifications are:\n doctorate, for example doctor of philosophy (PhD or DPhil)\n level 8 award\n level 8 certificate\n level 8 diploma",
    },
  ];

  useEffect(() => {
    refreshUnsavedState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamId]);

  useEffect(() => {
    const unsubscribe = form.subscribe?.(() => refreshUnsavedState());
    return () => unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, activeTeamId]);

  const openManageTeams = () => {
    setDraftTeams(
      teamsList.map((team) => ({
        ...team,
        _key: String(team.id),
        _isNew: false,
        _isEdited: false,
      }))
    );
    setNewTeamName("");
    setEditingTeamKey(null);
    setEditingTeamName("");
    setManageTeamsOpen(true);
  };

  const handleDraftAddTeam = () => {
    const title = newTeamName?.trim();
    if (!title) {
      toast.error("Team cannot be empty");
      return;
    }
    if (draftTeams.some((t) => t.title?.trim().toLowerCase() === title.toLowerCase())) {
      toast.error("Team name already exists");
      return;
    }
    setDraftTeams((prev) => [
      ...prev,
      {
        _key: `new-${Date.now()}`,
        title,
        _isNew: true,
        _isEdited: false,
      },
    ]);
    setNewTeamName("");
    inputRef.current?.focus?.();
  };

  const handleDraftDeleteTeam = (key) => {
    setDraftTeams((prev) => prev.filter((t) => t._key !== key));
    if (editingTeamKey === key) {
      setEditingTeamKey(null);
      setEditingTeamName("");
    }
  };

  const startEditTeam = (team) => {
    setEditingTeamKey(team._key);
    setEditingTeamName(team.title || "");
  };

  const saveEditTeam = (key) => {
    const title = editingTeamName?.trim();
    if (!title) {
      toast.error("Team cannot be empty");
      return;
    }
    if (
      draftTeams.some(
        (t) => t._key !== key && t.title?.trim().toLowerCase() === title.toLowerCase()
      )
    ) {
      toast.error("Team name already exists");
      return;
    }
    setDraftTeams((prev) =>
      prev.map((t) =>
        t._key === key
          ? { ...t, title, _isEdited: !t._isNew ? true : t._isEdited }
          : t
      )
    );
    setEditingTeamKey(null);
    setEditingTeamName("");
  };

  const handleTeamDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = draftTeams.findIndex((t) => t._key === active.id);
    const newIndex = draftTeams.findIndex((t) => t._key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(draftTeams, oldIndex, newIndex);
    setDraftTeams(reordered);

    const payload = {};
    reordered.forEach((team, index) => {
      if (team?.id) {
        payload[team.id] = index;
      }
    });
    if (Object.keys(payload).length) {
      reArrangeOrder(
        userServiceApi.reArrangeMicrositeTitles({ micrositeId, tabConfigId }),
        payload
      ).then(() => {
        setTeamsList((prev) => {
          const byId = new Map(prev.map((t) => [String(t.id), t]));
          return reordered
            .filter((t) => t?.id && byId.has(String(t.id)))
            .map((t) => ({
              ...byId.get(String(t.id)),
              order: payload[t.id],
            }));
        });
      });
    }
  };

  const saveManageTeams = async () => {
    setSavingTeams(true);
    try {
      const originalById = new Map(teamsList.map((t) => [String(t.id), t]));
      const draftIds = new Set(
        draftTeams.filter((t) => t.id).map((t) => String(t.id))
      );

      const toDelete = teamsList.filter((t) => !draftIds.has(String(t.id)));
      for (const team of toDelete) {
        await deleteRequest(userServiceApi.deleteSectionTitle(team.id));
      }

      const toEdit = draftTeams.filter(
        (t) =>
          t.id &&
          t._isEdited &&
          originalById.get(String(t.id))?.title !== t.title
      );
      for (const team of toEdit) {
        await putRequest(
          userServiceApi.saveTeamsCoursesProducts(micrositeId, tabConfigId),
          [{ id: team.id, title: team.title }]
        );
      }

      const toAdd = draftTeams.filter((t) => t._isNew || !t.id);
      if (toAdd.length) {
        await putRequest(
          userServiceApi.addSectionTitle(micrositeId, tabConfigId),
          toAdd.map((t) => ({ title: t.title }))
        );
      }

      const freshTeams = await fetchTeamsSorted();
      const byId = new Map(freshTeams.map((t) => [String(t.id), t]));
      const byTitle = new Map(
        freshTeams.map((t) => [String(t.title).trim().toLowerCase(), t])
      );

      const orderedPayload = {};
      draftTeams.forEach((draft, index) => {
        const match = draft.id
          ? byId.get(String(draft.id))
          : byTitle.get(String(draft.title).trim().toLowerCase());
        if (match?.id) {
          orderedPayload[match.id] = index;
        }
      });

      if (Object.keys(orderedPayload).length) {
        try {
          await putRequest(
            userServiceApi.reArrangeMicrositeTitles({
              micrositeId,
              tabConfigId,
            }),
            orderedPayload
          );
          await fetchTeamsSorted();
        } catch (_e) {
          // Keep draft order locally if rearrange API fails
          const orderedLocal = draftTeams
            .map((draft) => {
              const match = draft.id
                ? byId.get(String(draft.id))
                : byTitle.get(String(draft.title).trim().toLowerCase());
              return match;
            })
            .filter(Boolean);
          if (orderedLocal.length) {
            setTeamsList(orderedLocal);
          }
        }
      }

      if (
        activeTeamId &&
        toDelete.some((t) => String(t.id) === String(activeTeamId))
      ) {
        setActiveTeamId(null);
      }

      toast.success("Teams updated successfully");
      setManageTeamsOpen(false);
    } catch (error) {
      toast.error(error?.error || "Failed to update teams");
    } finally {
      setSavingTeams(false);
    }
  };

  const reorderTeamRoles = ({ active, over, fields: teamFields }) => {
    if (!over || active?.id === over?.id) return;

    const oldIndex = teamFields.findIndex((f) => f.key === active?.id);
    const newIndex = teamFields.findIndex((f) => f.key === over?.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const allValues = [...(form.getFieldValue(whichTab) || [])];
    const teamRoles = teamFields.map((f) => allValues[f.name]);
    const reorderedTeamRoles = arrayMove(teamRoles, oldIndex, newIndex);

    let teamPtr = 0;
    const result = allValues.map((item) => {
      if (String(item?.assetTitleId) === String(activeTeamId)) {
        return reorderedTeamRoles[teamPtr++];
      }
      return item;
    });

    form.setFieldsValue({ [whichTab]: result });

    const payload = {};
    result.forEach((e, index) => {
      if (e?.id) {
        payload[e.id] = index;
      }
    });
    if (Object.keys(payload).length) {
      reArrangeOrder(
        userServiceApi.reArrangeCardsData({ micrositeId, tabConfigId }),
        payload
      );
    }
  };

  const activeTeamTitle = useMemo(
    () => teamsList.find((t) => String(t.id) === String(activeTeamId))?.title,
    [teamsList, activeTeamId]
  );

  if (dataLoader || postLoader || loader) {
    return (
      <div className="data-loader">
        <Spin tip="Loading" size="large" />
      </div>
    );
  }

  return (
    <div className="newContantStyle teams-roles-page">
      <Row className="rowBody" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        {tabKey === whichTab ? (
          <Col className="gutter-row tabContent" span={24}>
            <div className="teams-section">
              <div className="teams-toolbar">
                <div className="teams-tabs-wrap">
                  {teamsList.length ? (
                    <Tabs
                      type="card"
                      className="teams-roles-team-tabs"
                      activeKey={activeTeamId ? String(activeTeamId) : undefined}
                      onChange={(key) => {
                        setActiveTeamId(key);
                        setExpandedRoleKey(null);
                      }}
                    >
                      {teamsList.map((team) => (
                        <Tabs.TabPane tab={team.title} key={String(team.id)} />
                      ))}
                    </Tabs>
                  ) : (
                    <div className="teams-empty-message">
                      No teams yet. Click Add/Edit Team to create one.
                    </div>
                  )}
                </div>
                <div className="teams-roles-actions">
                  <Button className="greenBtn" onClick={openManageTeams}>
                    Add/Edit Team
                  </Button>
                </div>
              </div>
            </div>

            <div className="teams-roles-section-divider" aria-hidden="true" />

            <Form.List
              name={whichTab}
              initialValue={
                tabData && tabData[whichTab] && tabData[whichTab].length > 0
                  ? withGifDefaults(sortRolesForDisplay(tabData[whichTab]))
                  : []
              }
            >
              {(fields, { remove }) => {
                const allValues = form.getFieldValue(whichTab) || [];
                const teamFields = fields.filter((field) => {
                  const item = allValues[field.name];
                  return String(item?.assetTitleId) === String(activeTeamId);
                });

                return (
                  <div className="roles-section">
                    <div className="roles-section-header">
                      {hasUnsavedItem && (
                        <Button
                          className="cancelBtn"
                          type="button"
                          onClick={handleClear}
                        >
                          Clear
                        </Button>
                      )}
                      <Button className="greenBtn" onClick={handleAddMore}>
                        Add/Edit Role <PlusOutlined />
                      </Button>
                    </div>

                    {!activeTeamId ? (
                      <div className="roles-empty-state">
                        Create a team to start adding roles.
                      </div>
                    ) : teamFields.length === 0 ? (
                      <div className="roles-empty-state">
                        No roles for {activeTeamTitle || "this team"} yet. Click
                        Add/Edit Role to create one.
                      </div>
                    ) : null}

                    <DragAndDropList
                      fields={teamFields}
                      form={form}
                      fieldName={whichTab}
                      showHandle={false}
                      enabled={teamFields.length > 0}
                      onDragEndCustom={reorderTeamRoles}
                      renderItem={({ key, name, ...restField }, _i, dnd = {}) => {
                        const i = name;
                        const roleName =
                          form.getFieldValue([whichTab, i, "subTitle"]) ||
                          "New Role";
                        const lastTeamField = teamFields[teamFields.length - 1];
                        const isExpanded =
                          expandedRoleKey === key ||
                          (expandedRoleKey === "new-role" &&
                            !form.getFieldValue([whichTab, i, "id"]) &&
                            lastTeamField?.key === key);
                        const { attributes = {}, listeners = {} } = dnd;

                        return (
                          <div className="role-accordion-item" key={key}>
                            <div
                              className={`role-accordion-header ${
                                isExpanded ? "expanded" : ""
                              }`}
                            >
                              <span
                                className="role-drag-handle"
                                {...attributes}
                                {...listeners}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DragOutlined />
                              </span>
                              <span
                                className="role-accordion-title"
                                onClick={() =>
                                  setExpandedRoleKey(isExpanded ? null : key)
                                }
                              >
                                {roleName}
                              </span>
                              <div className="role-accordion-actions">
                                <span
                                  onClick={() =>
                                    setExpandedRoleKey(isExpanded ? null : key)
                                  }
                                >
                                  {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                </span>
                                <Popconfirm
                                  title="Are you sure you want to delete this role?"
                                  onConfirm={(e) => {
                                    e?.stopPropagation?.();
                                    const id = form.getFieldValue([
                                      whichTab,
                                      i,
                                      "id",
                                    ]);
                                    if (id) {
                                      deleteSingleItem(whichTab, id, form);
                                    } else {
                                      remove(name);
                                      setHasUnsavedItem(false);
                                      refreshUnsavedState();
                                    }
                                    if (expandedRoleKey === key) {
                                      setExpandedRoleKey(null);
                                    }
                                  }}
                                  okText="Yes"
                                  cancelText="No"
                                  onCancel={(e) => e?.stopPropagation?.()}
                                >
                                  <DeleteOutlined
                                    className="role-delete-icon"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </Popconfirm>
                              </div>
                            </div>

                            {isExpanded ? (
                              <div className="role-accordion-body">
                                <div className="card-item">
                                  <Col
                                    className="gutter-row tabContent"
                                    xs={24}
                                    sm={24}
                                    md={14}
                                    lg={14}
                                    style={{ paddingRight: "25px" }}
                                  >
                                    <Form.Item
                                      {...restField}
                                      name={[name, "assetTitleId"]}
                                      hidden
                                      initialValue={activeTeamId}
                                    >
                                      <Input />
                                    </Form.Item>

                                    <Form.Item
                                      label="Role"
                                      {...restField}
                                      labelCol={{ span: 8 }}
                                      name={[name, "subTitle"]}
                                    >
                                      <Input
                                        placeholder="Enter Role"
                                        maxLength={40}
                                        showCount={{
                                          formatter: ({ count }) => `${count}/40`,
                                        }}
                                        onChange={() => {
                                          setRefreshKey((prev) => prev + 1);
                                          refreshUnsavedState();
                                        }}
                                      />
                                    </Form.Item>

                                    <div className="d-flex">
                                      <Form.Item
                                        {...restField}
                                        name={[name, "levelId"]}
                                        className="w-100"
                                        label="Select qualification level required"
                                        labelCol={{ span: 8 }}
                                      >
                                        <Select
                                          value={selectedLevel}
                                          onChange={(value) => {
                                            setSelectedLevel(value);
                                          }}
                                          allowClear
                                          placeholder="Select Level"
                                        >
                                          {levels?.map((level) => {
                                            const levelInfo = levelsData?.find(
                                              (item) => item?.value === level?.value
                                            );

                                            return (
                                              <Option
                                                key={level?.id}
                                                value={level?.id.toString()}
                                              >
                                                <Tooltip
                                                  overlay={
                                                    <div>
                                                      {levelInfo?.additionalInfo
                                                        ?.split("\n")
                                                        ?.map((line, index) => (
                                                          <div key={index}>
                                                            {line}
                                                          </div>
                                                        ))}
                                                    </div>
                                                  }
                                                  placement="right"
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        width: "10px",
                                                        height: "10px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          levelColor[
                                                            levelIndexMap[level?.value]
                                                          ],
                                                        marginRight: "8px",
                                                        marginLeft: "8px",
                                                      }}
                                                    />
                                                    {level.value}
                                                  </div>
                                                </Tooltip>
                                              </Option>
                                            );
                                          })}
                                        </Select>
                                      </Form.Item>
                                    </div>

                                    <Form.Item
                                      label="Description"
                                      {...restField}
                                      labelCol={{ span: 8 }}
                                      name={[name, "description"]}
                                      rules={[
                                        {
                                          required: true,
                                          message: "Description is required",
                                        },
                                        {
                                          max: MAX_CHARACTERS - 1,
                                          message: `Description should be less than ${MAX_CHARACTERS} characters`,
                                        },
                                      ]}
                                    >
                                      <TextArea
                                        placeholder="Enter Description"
                                        rows={5}
                                        maxLength={MAX_CHARACTERS}
                                        showCount
                                      />
                                    </Form.Item>

                                    <div className="d-flex">
                                      <Form.Item
                                        {...restField}
                                        name={[name, "mediaLink", "mediaType"]}
                                        className="w-100"
                                        label="Video or Image"
                                        labelCol={{ span: 8 }}
                                      >
                                        <Select
                                          placeholder="Select Option"
                                          allowClear
                                          onChange={(e) =>
                                            SelectChangeHandler(e, i)
                                          }
                                        >
                                          <Select.Option value="VIMEO_MEDIA">
                                            Add Vimeo Link
                                          </Select.Option>
                                          <Select.Option value="SOCIAL_MEDIA">
                                            Add Youtube Link
                                          </Select.Option>
                                          <Select.Option value="SYNTHESIA_MEDIA">
                                            Add Synthesia Link
                                          </Select.Option>
                                          <Select.Option value="IMAGE">
                                            Upload Image
                                          </Select.Option>
                                          <Select.Option value="GIF">
                                            Create GIF
                                          </Select.Option>
                                        </Select>
                                      </Form.Item>
                                      <Form.Item>
                                        <div className="remove-btn">
                                          <Tooltip title="Upload Video link or image with size lesser than 2.5MB. For best results, use images and videos with a 16:9 aspect ratio">
                                            <QuestionCircleOutlined />
                                          </Tooltip>
                                        </div>
                                      </Form.Item>
                                    </div>

                                    {findValue(i, "type") === "SOCIAL_MEDIA" ? (
                                      <Form.Item
                                        label="Youtube Link"
                                        labelCol={{ span: 8 }}
                                        {...restField}
                                        name={[name, "mediaLink", "url"]}
                                      >
                                        <Input />
                                      </Form.Item>
                                    ) : findValue(i, "type") === "VIMEO_MEDIA" ? (
                                      <Form.Item
                                        label="Vimeo Link"
                                        labelCol={{ span: 8 }}
                                        {...restField}
                                        name={[name, "mediaLink", "url"]}
                                      >
                                        <Input />
                                      </Form.Item>
                                    ) : findValue(i, "type") === "IMAGE" ? (
                                      <Form.Item
                                        {...restField}
                                        label="Image"
                                        colon={false}
                                        name="media"
                                        className="experienceImg"
                                        labelCol={{ span: 8 }}
                                      >
                                        {findValue(i, "blob") ? (
                                          <Avatar
                                            src={URL.createObjectURL(
                                              findValue(i, "blob")
                                            )}
                                            alt=""
                                          />
                                        ) : findValue(i, "url") ? (
                                          <Avatar
                                            src={findValue(i, "url")}
                                            alt=""
                                          />
                                        ) : (
                                          <Avatar src={UserIcon} />
                                        )}
                                        <label
                                          name="file"
                                          className="fileUploadBtn greenBtn"
                                        >
                                          Upload Image
                                          <input
                                            accept="image/png, image/gif, image/jpeg"
                                            type="file"
                                            onChange={(e) => {
                                              updateImage(e, i, form, whichTab);
                                              setTimeout(
                                                () =>
                                                  setRefreshKey((prev) => prev + 1),
                                                200
                                              );
                                            }}
                                          />
                                        </label>
                                        <Tooltip
                                          className="question"
                                          title={
                                            "Recommended image dimensions: 1280x720 pixels"
                                          }
                                        >
                                          <QuestionCircleOutlined />
                                        </Tooltip>
                                      </Form.Item>
                                    ) : findValue(i, "type") === "GIF" ? (
                                      <Form.Item
                                        {...restField}
                                        label="Create GIF"
                                        colon={false}
                                        name={[name, "mediaLink", "gifCreator"]}
                                        className="gif-creator-field"
                                        labelCol={{ span: 8 }}
                                        initialValue={createEmptyGifState()}
                                      >
                                        <GifCreator />
                                      </Form.Item>
                                    ) : findValue(i, "type") ===
                                      "SYNTHESIA_MEDIA" ? (
                                      <Form.Item
                                        label="Synthesia Link"
                                        labelCol={{ span: 8 }}
                                        {...restField}
                                        name={[name, "mediaLink", "url"]}
                                      >
                                        <Input />
                                      </Form.Item>
                                    ) : null}

                                    <Form.Item
                                      className="w-100"
                                      style={{
                                        display: "flex",
                                        justifyContent: "end",
                                      }}
                                    >
                                      {formChanged ? (
                                        <Button
                                          className="saveBtn greenBtn"
                                          onClick={() => {
                                            // Ensure selected team title id is always in payload
                                            form.setFieldValue(
                                              [whichTab, i, "assetTitleId"],
                                              activeTeamId
                                            );
                                            form.submit();
                                            setCanAddNew(true);
                                            setHasUnsavedItem(false);
                                          }}
                                        >
                                          Save
                                        </Button>
                                      ) : (
                                        <Button
                                          className="saveBtn greenBtn"
                                          style={{
                                            opacity: "0.5",
                                            pointerEvents: "none",
                                          }}
                                        >
                                          Save
                                        </Button>
                                      )}
                                    </Form.Item>
                                  </Col>

                                  <Col
                                    className="gutter-row tabContent"
                                    xs={24}
                                    sm={24}
                                    md={10}
                                    lg={10}
                                  >
                                    <Form.Item shouldUpdate noStyle>
                                      {() => {
                                        const row = form.getFieldValue(whichTab)?.[i];
                                        if (!row) return null;
                                        return (
                                          <div key={refreshKey}>
                                            {renderMedia(row, i)}
                                          </div>
                                        );
                                      }}
                                    </Form.Item>
                                  </Col>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      }}
                    />
                  </div>
                );
              }}
            </Form.List>
          </Col>
        ) : null}
      </Row>

      <Modal
        title="Manage Teams"
        visible={manageTeamsOpen}
        onCancel={() => setManageTeamsOpen(false)}
        destroyOnClose
        width={520}
        className="manage-teams-modal"
        footer={[
          <Button key="close" onClick={() => setManageTeamsOpen(false)}>
            Close
          </Button>,
          <Button
            key="save"
            className="greenBtn"
            loading={savingTeams}
            onClick={saveManageTeams}
          >
            Save Changes
          </Button>,
        ]}
      >
        <div className="manage-teams-list">
          <DndContext onDragEnd={handleTeamDragEnd}>
            <SortableContext
              items={draftTeams.map((t) => t._key)}
              strategy={verticalListSortingStrategy}
            >
              {draftTeams.map((team) => (
                <SortableTeamRow key={team._key} team={team}>
                  {({ setNodeRef, attributes, listeners, style }) => (
                    <div
                      ref={setNodeRef}
                      style={style}
                      className="manage-team-row"
                    >
                      <span
                        className="team-drag-handle"
                        {...attributes}
                        {...listeners}
                      >
                        <DragOutlined />
                      </span>
                      {editingTeamKey === team._key ? (
                        <Input
                          className="team-name-input"
                          value={editingTeamName}
                          maxLength={40}
                          showCount={{
                            formatter: ({ count }) => `${count}/40`,
                          }}
                          onChange={(e) => setEditingTeamName(e.target.value)}
                          onPressEnter={() => saveEditTeam(team._key)}
                        />
                      ) : (
                        <span className="team-name">{team.title}</span>
                      )}
                      <div className="team-row-actions">
                        {editingTeamKey === team._key ? (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => saveEditTeam(team._key)}
                          >
                            Done
                          </Button>
                        ) : (
                          <EditOutlined
                            title="Edit"
                            onClick={() => startEditTeam(team)}
                          />
                        )}
                        <Popconfirm
                          title="Are you sure you want to delete this team?"
                          onConfirm={() => handleDraftDeleteTeam(team._key)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <DeleteOutlined title="Delete" />
                        </Popconfirm>
                      </div>
                    </div>
                  )}
                </SortableTeamRow>
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <div className="manage-teams-add">
          <Input
            ref={inputRef}
            placeholder="Enter new team name..."
            value={newTeamName}
            maxLength={40}
            showCount={{
              formatter: ({ count }) => `${count}/40`,
            }}
            onChange={(e) => setNewTeamName(e.target.value)}
            onPressEnter={handleDraftAddTeam}
          />
          <Button className="greenBtn" onClick={handleDraftAddTeam}>
            Add
          </Button>
        </div>
      </Modal>
    </div>
  );
}
